import { Energy, Facility, HeatSource, RefrigerantSource, WaterSource } from "../../../../api/facility";
import { FACILITY_OWNERSHIP, FACILITY_TYPES, UNIT_TYPES } from "../../../../config/constants";
import {
  DEFAULT_ENARGY_SOURCE,
  DEFAULT_HEAT_SOURCE,
  DEFAULT_REFIGERATION_SOURCE,
  DEFAULT_WATER_SOURCE
} from "../../../../config/drawingConstants";
import { EnergyDraft, FacilityDraftFormValues, VolumeDraft } from "../types/facilityDrawer.types";

export const createEmptyEnergyDraft = (): EnergyDraft => ({
  EnergyType: String(DEFAULT_ENARGY_SOURCE),
  Year: "",
  kWhConsumed: "",
  Percentage: "",
  Price: ""
});

export const createEmptyHeatDraft = (): VolumeDraft => ({
  SourceId: String(DEFAULT_HEAT_SOURCE),
  Year: "",
  Volumes: "",
  UnitType: UNIT_TYPES[0].value,
  Price: ""
});

export const createEmptyWaterDraft = (): VolumeDraft => ({
  SourceId: String(DEFAULT_WATER_SOURCE),
  Year: "",
  Volumes: "",
  UnitType: "",
  Price: ""
});

export const createEmptyRefrigerantDraft = (): VolumeDraft => ({
  SourceId: String(DEFAULT_REFIGERATION_SOURCE),
  Year: "",
  Volumes: "",
  UnitType: UNIT_TYPES[0].value,
  Price: ""
});

// Every source section always shows at least one record card — matching the prototype, which
// opens with "Record 1" visible and empty rather than a "no records" hint. "+ Add record" appends
// further cards; the trash icon can remove down to zero, at which point the empty hint reappears.
const emptyFacilityDraft = (defaultFacilityType: string): FacilityDraftFormValues => ({
  facilityName: "",
  facilityOwnership: FACILITY_OWNERSHIP[0].value,
  facilityType: defaultFacilityType,
  facilityCountry: "",
  facilityAddress: "",
  facilityCity: "",
  energy: [createEmptyEnergyDraft()],
  heatSource: [createEmptyHeatDraft()],
  waterSource: [createEmptyWaterDraft()],
  refrigerantSource: [createEmptyRefrigerantDraft()]
});

export const createEmptyRegisterDraft = (): FacilityDraftFormValues =>
  emptyFacilityDraft(FACILITY_TYPES[2].value);

// A row is "blank" when the user hasn't touched anything beyond its pre-filled defaults
// (SourceId/UnitType always carry a default; Year/volume/price fields start empty). Blank rows
// are stripped right before submit-time validation so a section's default record card doesn't
// force the user to fill it in — or delete it — before they can save an otherwise-valid facility.
export const isEnergyRowBlank = (row: EnergyDraft): boolean =>
  !row.id && !row.Year && !row.kWhConsumed && !row.Percentage && !row.Price;

export const isVolumeRowBlank = (row: VolumeDraft): boolean =>
  !row.id && !row.Year && !row.Volumes && !row.Price;

const toDraftString = (value: unknown): string =>
  value === undefined || value === null ? "" : String(value);

const mapEnergyToDraft = (item: Energy): EnergyDraft => ({
  id: item.id,
  EnergyType: toDraftString(item.EnergyType),
  Year: toDraftString(item.Year),
  kWhConsumed: toDraftString(item.kWhConsumed),
  Percentage: toDraftString(item.Percentage),
  Price: toDraftString(item.Price)
});

const mapHeatToDraft = (item: HeatSource): VolumeDraft => ({
  id: item.id,
  SourceId: toDraftString(item.HeatSource),
  Year: toDraftString(item.Year),
  Volumes: toDraftString(item.Volumes),
  UnitType: item.UnitType ?? "",
  Price: toDraftString(item.Price),
  Percentage: item.Percentage !== undefined ? toDraftString(item.Percentage) : undefined
});

const mapWaterToDraft = (item: WaterSource): VolumeDraft => ({
  id: item.id,
  SourceId: toDraftString(item.WaterSource),
  Year: toDraftString(item.Year),
  Volumes: toDraftString(item.Volumes),
  UnitType: "",
  Price: toDraftString(item.Price)
});

const mapRefrigerantToDraft = (item: RefrigerantSource): VolumeDraft => ({
  id: item.id,
  SourceId: toDraftString(item.RefrigerantSource),
  Year: toDraftString(item.Year),
  Volumes: toDraftString(item.Volumes),
  UnitType: item.UnitType ?? "",
  Price: toDraftString(item.Price),
  Percentage: item.Percentage !== undefined ? toDraftString(item.Percentage) : undefined
});

export const mapFacilityToDraft = (facility: Partial<Facility> | undefined): FacilityDraftFormValues => {
  if (!facility) {
    return emptyFacilityDraft(FACILITY_TYPES[0].value);
  }

  const energy = (facility.energy ?? []).map(mapEnergyToDraft);
  const heatSource = (facility.heatSource ?? []).map(mapHeatToDraft);
  const waterSource = (facility.waterSource ?? []).map(mapWaterToDraft);
  const refrigerantSource = (facility.refrigerantSource ?? []).map(mapRefrigerantToDraft);

  return {
    facilityName: facility.facilityName ?? "",
    facilityOwnership: facility.facilityOwnership || FACILITY_OWNERSHIP[0].value,
    facilityType: facility.facilityType || FACILITY_TYPES[0].value,
    facilityCountry: toDraftString(facility.facilityCountry),
    facilityAddress: facility.facilityAddress ?? "",
    facilityCity: toDraftString(facility.facilityCity),
    // Editing shows the same "always at least one record card" pattern as registering.
    energy: energy.length > 0 ? energy : [createEmptyEnergyDraft()],
    heatSource: heatSource.length > 0 ? heatSource : [createEmptyHeatDraft()],
    waterSource: waterSource.length > 0 ? waterSource : [createEmptyWaterDraft()],
    refrigerantSource: refrigerantSource.length > 0 ? refrigerantSource : [createEmptyRefrigerantDraft()]
  };
};

// Note: Energy/HeatSource/WaterSource/RefrigerantSource declare their *Source id fields as
// `string`, but the legacy add-record handlers actually persist them as numbers
// (`Number(defaultX) || DEFAULT_X`). Matched here via `as unknown as Facility` at the payload
// boundary so the API receives the same shape legacy always sent.
const buildEnergyRecord = (draft: EnergyDraft): Energy => ({
  ...(draft.id ? { id: draft.id } : {}),
  Year: Number(draft.Year),
  Percentage: Number(draft.Percentage),
  Price: draft.Price ? Number(draft.Price) : 0,
  kWhConsumed: Number(draft.kWhConsumed),
  EnergyType: (Number(draft.EnergyType) || DEFAULT_ENARGY_SOURCE) as unknown as string
} as Energy);

const buildHeatRecord = (draft: VolumeDraft): HeatSource =>
  ({
    ...(draft.id ? { id: draft.id } : {}),
    Year: Number(draft.Year),
    ...(draft.Percentage !== undefined ? { Percentage: Number(draft.Percentage) } : {}),
    Price: draft.Price ? Number(draft.Price) : 0,
    Volumes: Number(draft.Volumes),
    UnitType: draft.UnitType,
    HeatSource: (Number(draft.SourceId) || DEFAULT_HEAT_SOURCE) as unknown as string
  } as HeatSource);

const buildWaterRecord = (draft: VolumeDraft): WaterSource =>
  ({
    ...(draft.id ? { id: draft.id } : {}),
    Year: Number(draft.Year),
    Price: draft.Price ? Number(draft.Price) : 0,
    Volumes: Number.isNaN(Number(draft.Volumes)) ? 0 : Number(draft.Volumes),
    WaterSource: (Number(draft.SourceId) || DEFAULT_WATER_SOURCE) as unknown as string
  } as WaterSource);

const buildRefrigerantRecord = (draft: VolumeDraft): RefrigerantSource =>
  ({
    ...(draft.id ? { id: draft.id } : {}),
    Year: Number(draft.Year),
    ...(draft.Percentage !== undefined ? { Percentage: Number(draft.Percentage) } : {}),
    Price: draft.Price ? Number(draft.Price) : 0,
    Volumes: Number(draft.Volumes),
    UnitType: draft.UnitType,
    RefrigerantSource: (Number(draft.SourceId) || DEFAULT_REFIGERATION_SOURCE) as unknown as string
  } as RefrigerantSource);

const buildFacilitySourcePayload = (draft: FacilityDraftFormValues) => ({
  facilityName: draft.facilityName,
  facilityAddress: draft.facilityAddress,
  facilityCity: draft.facilityCity,
  facilityCountry: draft.facilityCountry,
  facilityType: draft.facilityType,
  facilityOwnership: draft.facilityOwnership,
  energy: draft.energy.map(buildEnergyRecord),
  heatSource: draft.heatSource.map(buildHeatRecord),
  waterSource: draft.waterSource.map(buildWaterRecord),
  refrigerantSource: draft.refrigerantSource.map(buildRefrigerantRecord)
});

export const buildCreateFacilityPayload = (draft: FacilityDraftFormValues): Facility =>
  ({
    ...buildFacilitySourcePayload(draft),
    originalId: null
  } as unknown as Facility);

export const buildEditFacilityPayload = (
  draft: FacilityDraftFormValues,
  facilityId: string | undefined
): Facility =>
  ({
    _id: facilityId,
    ...buildFacilitySourcePayload(draft)
  } as unknown as Facility);
